import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { ContenderBonus } from '#src/components/lineup/Contender/ContenderFooter/ContenderBonus/index.tsx';
import { LineupBonusDetails } from '#src/components/lineup/LineupBonusDetails/index.tsx';

import type { TaskLineupScore_taskLineupInterface } from './__generated__/index.graphql.ts';

type Props = {
  lineup: TaskLineupScore_taskLineupInterface;
  roundScore?: boolean;
  hideBonus?: boolean;
};

export const TaskLineupScore = ({
  lineup,
  roundScore = false,
  hideBonus = false,
}: Props) => {
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const { score, bonusPoints, bonuses, taskAppearances } = lineup;

  return (
    <>
      <Horizontal center>
        {!hideBonus && (
          <ButtonBase
            onClick={() => {
              setBonusDialogOpen(true);
            }}
          >
            <ContenderBonus
              bonusRating={null}
              bonus={bonusPoints}
              withSetColor
            />
          </ButtonBase>
        )}
        <BrandM>
          <FormattedMessage
            {...fantasy.ordinalPoints}
            values={{
              score: (
                <FormattedNumber
                  value={roundScore ? Math.round(score) : score}
                  maximumFractionDigits={roundScore ? 0 : 2}
                />
              ),
              sup: c => (
                <BrandXXS as="sup" color="var(--c-nd-600)">
                  {c}
                </BrandXXS>
              ),
            }}
          />
        </BrandM>
      </Horizontal>
      {!!bonusDialogOpen && !!bonuses && !!taskAppearances && (
        <Dialog
          open={bonusDialogOpen}
          onClose={() => {
            setBonusDialogOpen(false);
          }}
        >
          <LineupBonusDetails
            bonuses={bonuses}
            appearances={taskAppearances}
            initiallySelectedAppearanceIndex={0}
            showXpBalance={false}
            isSet={true}
          />
        </Dialog>
      )}
    </>
  );
};

TaskLineupScore.fragments = {
  taskLineupInterface: gql`
    fragment TaskLineupScore_taskLineupInterface on TaskLineupInterface {
      id
      score
      bonusPoints
      bonuses {
        ...LineupBonusDetails_so5AppearanceBonusInterface
      }
      taskAppearances {
        id
        index
        ...LineupBonusDetails_appearanceInterface
      }
    }
    ${LineupBonusDetails.fragments.so5AppearanceBonusInterface}
    ${LineupBonusDetails.fragments.appearanceInterface}
  ` as TypedDocumentNode<TaskLineupScore_taskLineupInterface>,
};
